import { styled } from '@linaria/react';
import { Group } from '@mantine/core';
import { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { EditableTextField } from '../../editable-text-field';
import type { BoardSetting, CreateBoardSettingPayload } from './board-settings-client.type';
import { BoardSettingAction } from './board-settings-reducer';

export function CreateBoardSettingsHeader({
  state,
  dispatch,
  setPageState,
  isDirty,
  boardSettings,
  createBoardSetting,
  hasPendingBoards,
  pendingChanges,
}: {
  state: BoardSetting;
  dispatch: Dispatch<BoardSettingAction>;
  setPageState: (pageState: 'update' | 'create' | 'idle') => void;
  isDirty: boolean;
  boardSettings: BoardSetting[];
  createBoardSetting: (boardSetting: CreateBoardSettingPayload) => void;
  hasPendingBoards: boolean;
  pendingChanges: Partial<CreateBoardSettingPayload>;
}) {
  const i18n = useTranslation();
  const organization = useGlobalStore((state) => state.organization);

  return (
    <HeaderContainer>
      <EditableTextField
        value={state.name || 'New Board Settings'}
        canEdit
        isOpen
        onSave={(value: string) => {
          dispatch({
            type: 'UPDATE_FIELD',
            field: 'name',
            value,
          });
        }}
        onCancel={() => {}}
      />
      <Group>
        <Button
          variant="outline"
          size="xs"
          radius="xl"
          style={{ padding: '8px 16px', gap: '8px' }}
          onClick={() => {
            const hasChanges = isDirty || hasPendingBoards || Boolean(Object.keys(pendingChanges || {}).length > 0);
            const selectedBoardSetting =
              (boardSettings || []).find((boardSetting) => boardSetting.id === state.id) || boardSettings[0];

            if (hasChanges) {
              if (window.confirm(i18n.t('strategy.initiatives.unsaved_changes'))) {
                dispatch({
                  type: 'SET_BOARD_SETTING',
                  payload: selectedBoardSetting ?? state,
                });
                setPageState('idle');
              }
            } else {
              dispatch({
                type: 'SET_BOARD_SETTING',
                payload: selectedBoardSetting ?? state,
              });
              setPageState('idle');
            }
          }}
        >
          <Text>{i18n.t('common.actions.cancel')}</Text>
        </Button>
        <Button
          disabled={!isDirty || !state.name}
          variant="primary"
          size="xs"
          radius="xl"
          leftSection={<Icon name="check_circle_outline" variant="filled" size={16} color="white" />}
          style={{ padding: '8px 16px', gap: '8px' }}
          onClick={() => {
            createBoardSetting({
              organization_id: organization?.id || '',
              name: state.name,
              label_for_card_mandatory: pendingChanges.label_for_card_mandatory ?? state.label_for_card_mandatory,
              methodology: pendingChanges.methodology ?? state.methodology,
              points_for_bug_cards: pendingChanges.points_for_bug_cards ?? state.points_for_bug_cards,
              working_hours_end: pendingChanges.working_hours_end ?? state.working_hours_end,
              working_hours_start: pendingChanges.working_hours_start ?? state.working_hours_start,
            });
          }}
        >
          <Text>{i18n.t('common.actions.save')}</Text>
        </Button>
      </Group>
    </HeaderContainer>
  );
}

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;
